import React from 'react';
import { Inputs } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
import { PestRepEconomics as PestRepEconomicsData, PestRepRow } from '../utils/pestRepEconomics';
interface PestRepEconomicsProps {
  economics: PestRepEconomicsData;
  inputs: Inputs;
  halfSales: boolean;
  doubleAttrition: boolean;
}

// Pest and Summer Pest: the cost to recruit one rep who clears day 90,
// against what that rep is worth. Manager view: total overrides (including
// the retroactive backend) for a rep who sells 3 or 6 months. Company view:
// cumulative gross profit at years one, two and three from one season.
export function PestRepEconomics({
  economics,
  inputs,
  halfSales,
  doubleAttrition
}: PestRepEconomicsProps) {
  const {
    mode,
    spend,
    signed,
    day90Reps,
    costPerDay90Rep,
    hiresPerDay90Rep,
    sellingMonths,
    rows,
    paybackMonths
  } = economics;
  const manager = mode === 'manager';
  const scaleMax = Math.max(costPerDay90Rep, ...rows.map(r => r.profit));
  const costPosition = scaleMax > 0 ? costPerDay90Rep / scaleMax * 100 : 0;
  const attritionNote = inputs.revenueCadence === 'quarterly' ? `${inputs.quarterlyAttrition || 5}% a quarter` : `${inputs.monthlyAttrition || 2}% a month`;
  const notes: string[] = [];
  if (halfSales) notes.push('50% Sales halves what each rep sells');
  if (doubleAttrition) notes.push('2x Attrition halves the reps who clear day 90, so each one costs twice as much');
  return <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 lg:gap-10" data-id="element-957" data-mp="g3zkjyxx6ezp">
      {/* The cost every row is measured against */}
      <div className="lg:border-r lg:border-gray-200 lg:pr-10" data-id="element-958" data-mp="tw7frm9k1xwa">
        <div className="text-sm font-semibold text-gray-600" data-id="element-959" data-mp="p2kythwcz7uo">
          Cost to recruit one rep who clears day 90
        </div>
        <div className="mt-1 text-3xl md:text-4xl font-extrabold text-navy tabular-nums" data-id="element-960" data-mp="1bpboy2ul0tr">
          {fmtMoney(costPerDay90Rep)}
        </div>
        <p className="mt-3 text-sm text-gray-600" data-id="element-961" data-mp="1whhypuubkv17">
          {fmtMoney(spend)} spent recruiting in year one (implementation, D2D
          HIRE fees, ads, est. bonuses) ÷ {day90Reps.toFixed(1)} of the{' '}
          {Math.round(signed)} reps signed who clear day 90. About{' '}
          {hiresPerDay90Rep.toFixed(1)} signs for every rep who makes it, and
          every washout is paid for in that number.
        </p>
        {!manager && paybackMonths !== null && <div className="mt-5 border-t border-gray-200 pt-4" data-id="element-962" data-mp="1dkgtc8wusswq">
            <div className="text-sm font-semibold text-gray-600" data-id="element-963" data-mp="1ihpcabptt0z1">
              Rep pays for their own recruiting
            </div>
            <div className="mt-1 text-3xl font-extrabold text-green-600 tabular-nums" data-id="element-964" data-mp="1nimtakz3noyy">
              {paybackMonths} {paybackMonths === 1 ? 'month' : 'months'}
            </div>
            <div className="mt-1 text-sm text-gray-600" data-id="element-965" data-mp="2yv5r5x3q5m41">
              after their first sale
            </div>
          </div>}
      </div>

      <div data-id="element-966" data-mp="s7okkrfcu1jg">
        <div className="text-sm font-semibold text-navy mb-3" data-id="element-967" data-mp="s7g993yvr4ad">
          {manager ? 'Your return on one rep, including the retroactive backend' : `Your return on one rep who clears day 90 and sells only in year one`}
        </div>
        <ul className="divide-y divide-gray-100 border-t border-gray-200" data-id="element-968" data-mp="if0pq27xq7nd">
          {rows.map(row => <RepRow key={row.key} row={row} manager={manager} cost={costPerDay90Rep} scaleMax={scaleMax} costPosition={costPosition} data-id="element-969" data-mp="8j7fwqllos9a" />)}
        </ul>
        <p className="mt-3 text-xs text-gray-500" data-id="element-970" data-mp="3voonzeyb9o0v">
          <span className="inline-block align-middle w-0.5 h-3 bg-navy mr-1.5" data-id="element-971" data-mp="3qp29l26qy1qh" />
          Marks the cost to recruit one day-90 rep. Accounts are after{' '}
          {inputs.pullthrough}% pull-through, and accounts cancel at{' '}
          {attritionNote}.{' '}
          {manager ? 'Totals include every upfront override, plus the backend paid once each account clears the clawback window, less overrides clawed back on early cancels. The rep starts in the launch month (or Month 1); paused sales months write no accounts, so they earn no upfront or backend.' : `The rep sells ${sellingMonths} month${sellingMonths === 1 ? '' : 's'} in year one (ramp, then the veteran rate), then stops; years two and three are the same accounts paying out. Gross profit is cumulative.`}{' '}
          The leader search isn&rsquo;t included.
          {notes.length > 0 && ` ${notes.join('; ')}.`}
        </p>
      </div>
    </div>;
}
interface RepRowProps {
  row: PestRepRow;
  manager: boolean;
  cost: number;
  scaleMax: number;
  costPosition: number;
}
function RepRow({
  row,
  manager,
  cost,
  scaleMax,
  costPosition
}: RepRowProps) {
  const barWidth = scaleMax > 0 ? row.profit / scaleMax * 100 : 0;
  const paysBack = row.profit >= cost;
  return <li className="grid grid-cols-[1fr_auto] sm:grid-cols-[150px_1fr_100px] items-center gap-x-5 gap-y-2 py-4" data-id="element-972" data-mp="3l0vivo3bu6ih">
      <div data-id="element-973" data-mp="31hy325j55u3c">
        <div className="text-sm font-semibold text-navy" data-id="element-974" data-mp="2xe9t5g71emex">{row.label}</div>
        <div className="text-xs text-gray-500" data-id="element-975" data-mp="2xg33p6krtqs7">{row.detail}</div>
        <div className="text-xs text-gray-500 tabular-nums" data-id="element-976" data-mp="3bf2no0xc9hi1">
          {Math.round(row.accounts)} accounts
        </div>
      </div>

      <div className="min-w-0 col-span-2 sm:col-span-1 order-3 sm:order-none" data-id="element-977" data-mp="36dn6ijya7v4x">
        <div className="flex flex-wrap items-baseline gap-x-3 tabular-nums" data-id="element-978" data-mp="3kdem2p4g5jl1">
          <span className="text-lg font-bold text-gray-900" data-id="element-979" data-mp="3gdxf1r9dgedr">
            {fmtMoney(row.profit)}
          </span>
          <span className="text-xs text-gray-500" data-id="element-980" data-mp="3u37oazip8mcm">
            {manager && row.overrides ? `${fmtMoney(row.overrides.upfront)} upfront + ${fmtMoney(row.overrides.backend)} backend − ${fmtMoney(row.overrides.clawbacks)} clawbacks` : `gross profit on ${fmtMoney(row.revenue)} revenue`}
          </span>
        </div>
        <div className="relative mt-2 h-2 rounded-full bg-gray-100" aria-hidden="true" data-id="element-981" data-mp="2wep217guxaq">
          <div className={`absolute inset-y-0 left-0 rounded-full ${paysBack ? 'bg-green-600' : 'bg-amber'}`} style={{
          width: `${Math.max(0, barWidth)}%`
        }} data-id="element-982" data-mp="f096nyx0d0oe" />
          <div className="absolute -top-1 -bottom-1 w-0.5 bg-navy" style={{
          left: `calc(${costPosition}% - 1px)`
        }} data-id="element-983" data-mp="oqc1m4nowopd" />
        </div>
      </div>

      <div className="text-right" data-id="element-984" data-mp="trhrzvtvnzvq">
        <div className={`text-xl font-bold tabular-nums ${paysBack ? 'text-green-600' : 'text-red-600'}`} data-id="element-985" data-mp="xqh5i4tod4ys">
          {row.multiple.toFixed(2)}x
        </div>
        <div className="text-xs text-gray-600 whitespace-nowrap tabular-nums" data-id="element-986" data-mp="17p6dn5vrrb1u">
          {paysBack ? 'Net ' : 'Short '}
          {fmtMoney(Math.abs(row.profit - cost))}
        </div>
      </div>
    </li>;
}